/**
 * PM-5 — bulk-turbine importer wire types, hand-rolled from
 * `contracts/projects/v1/openapi.yaml` (projects/v1@2.7.0, the
 * `project-import` tag). projects/v1 is NOT codegen'd in this repo (no
 * `api.projects.gen.ts`), so — exactly like `../types.ts` hand-rolls
 * `CreateProjectRequest` — the analyze/commit shapes are spelled out here
 * by hand. Match the contract schema names 1:1.
 *
 * SYNC: projects/v1@2.17.0 (project-import; importer live-grid rework adds
 * `rawRows`/`rawColumnCount` + per-row `assetExists`/`siteExists`; FB-32
 * replaces FB-30's per-row `overrideRowIndexes` with the granular per-CELL
 * `CommitImportBody.overrideFields` `{rowIndex, field}[]` override list).
 *
 * Steps 1–4 (this story) consume `AnalyzeImportReport` +
 * `RevalidateImportBody`. Commit (`CommitImportBody`) is shaped here too so
 * PM-6 can build it directly from the wizard's lifted state — but PM-5 does
 * NOT call commit.
 */

/** Per-row / per-cell validation severity (PM "Validation severity model").
 *  `hard_error` blocks the whole import; `needs_confirm` routes to the
 *  model create-and-confirm step (PM-D11, not an error); `override_block`
 *  blocks the row unless a tenant_admin confirms (PM-D4/D5) and carries a
 *  before/after diff (PM-D18); `info` never blocks. */
export const IMPORT_SEVERITIES = [
  "hard_error",
  "needs_confirm",
  "override_block",
  "info",
] as const;
export type ImportValidationSeverity = (typeof IMPORT_SEVERITIES)[number];

/** Field-level before/after diff for an `override_block` row (PM-D18). */
export interface ImportFieldDiff {
  field: string;
  /** The stored value (any JSON scalar / null). */
  current: unknown;
  /** The incoming value from the sheet (any JSON scalar / null). */
  incoming: unknown;
}

/** One column→field mapping entry (PM-D15). `field` is `null` for an
 *  ignored column. */
export interface ImportColumnMapping {
  columnIndex: number;
  columnHeader?: string | null;
  /** Target import field key (see {@link IMPORT_FIELD_KEYS}); `null` = ignore. */
  field: string | null;
  /** Auto-mapper confidence 0..1 (null for a manual mapping). */
  confidence?: number | null;
}

/** One parsed worksheet from the uploaded workbook (PM-D16). */
export interface ImportParsedSheet {
  sheetName: string;
  rowCount: number;
  suggestedHeaderRow?: number | null;
}

/** One parsed cell value (string-rendered for the grid). */
export interface ImportRowCell {
  columnIndex: number;
  value: string | null;
}

/** One RAW worksheet row from the active sheet, exactly as parsed — ALL
 *  columns, EVERY row including pre-header candidates (importer live-grid
 *  rework, projects/v1@2.13.0). The FE renders this grid for header-picking
 *  and mapping-with-data independent of the chosen header. Distinct from
 *  {@link AnalyzeImportReport.rows} (the validated, under-header data rows).
 *  Present on the analyze (parse) response; absent on the JSON revalidate. */
export interface ImportRawRow {
  /** 0-based row index within the active sheet (raw, NOT post-header). */
  rowIndex: number;
  /** One entry per non-empty-width column (sparse cells rendered as null). */
  cells: ImportRowCell[];
}

/** One validation finding on a row (or a specific cell). */
export interface ImportRowValidationMessage {
  severity: ImportValidationSeverity;
  /** Stable machine code, e.g. `missing_identifier`. */
  code: string;
  message: string;
  /** The import field the finding is about (null = row-level). */
  field?: string | null;
  /** Present (non-empty) only for `override_block` findings (PM-D18). */
  diff?: ImportFieldDiff[];
}

/** Per-company tenant-vs-contact resolution for a row's operator (PM-D23). */
export interface ImportCompanyResolution {
  field: string;
  rawValue: string | null;
  resolvesTo: "tenant" | "contact_only";
  tenantId?: string | null;
}

/** One analyzed data row: parsed cells, validation findings, company
 *  resolutions, and the per-row include/exclude flag (PM-D16). */
export interface ImportAnalyzedRow {
  rowIndex: number;
  included: boolean;
  cells: ImportRowCell[];
  messages: ImportRowValidationMessage[];
  companyResolutions?: ImportCompanyResolution[];
  /** True when this row's turbine `identifier` matched an EXISTING asset in
   *  the tenant (projects/v1@2.13.0). Lets the Review tree show **exists** vs
   *  **will-create**, distinct from `override_block`/`hard_error`. Omitted
   *  (treat as false) on excluded rows / the revalidate echo. */
  assetExists?: boolean;
  /** True when this row's `siteName` (park) matched an EXISTING site in the
   *  tenant (projects/v1@2.13.0). Lets the Review tree group parks as
   *  **exists** vs **will-create**. Omitted (treat as false) when absent. */
  siteExists?: boolean;
}

export interface ImportSummary {
  totalRows: number;
  hardErrorRows: number;
  overrideBlockRows: number;
  needsConfirmRows: number;
}

/** Dry-run analysis result for `POST /imports/analyze`. */
export interface AnalyzeImportReport {
  /** Opaque token echoing the server-side parse, so a later JSON
   *  re-validate need not re-upload the file. Null if the BE is stateless. */
  analysisToken?: string | null;
  sheets: ImportParsedSheet[];
  suggestedSheetName: string;
  suggestedHeaderRow?: number | null;
  mapping: ImportColumnMapping[];
  rows: ImportAnalyzedRow[];
  summary: ImportSummary;
  /** The ACTIVE sheet's RAW top-N rows (cap N≈200) — ALL columns, EVERY row
   *  INCLUDING pre-header candidates — for the unified live grid (header-pick
   *  + map-with-data). Present on the analyze (parse) response; ABSENT on the
   *  JSON revalidate response (revalidate does not re-parse the workbook). */
  rawRows?: ImportRawRow[];
  /** The active sheet's column count (the widest raw row). Lets the FE render
   *  a fixed-width grid without scanning every `rawRows` entry. Present
   *  alongside `rawRows` on the analyze response. */
  rawColumnCount?: number;
  /** CM-4 (CM-D14) — the contacts that WOULD be newly created on commit:
   *  `contact_only` party/operator companies (no known-tenant link) whose name
   *  has NO active address-book match. Matched (already existing) companies are
   *  excluded. Computed read-only on the INCLUDED rows; the commit then
   *  find-or-creates them (idempotent on re-import). Empty/absent when none.
   *  Drives the import-review "N new contacts will be created" callout (CM-8). */
  newContacts?: { count: number; names: string[] };
}

/** JSON re-validate body for `POST /imports/analyze` on a SUBSEQUENT pass:
 *  the user's edited mapping + toggled rows + edited cell values are
 *  re-validated WITHOUT a re-upload (PM-D6). */
export interface RevalidateImportBody {
  analysisToken?: string | null;
  sheetName: string;
  headerRow: number;
  mapping: ImportColumnMapping[];
  rows: ImportAnalyzedRow[];
}

/** Confirmed system-type → asset-model resolution (PM-D11). */
export interface ImportModelResolution {
  rawType: string;
  action: "use_existing" | "create_new";
  assetModelId?: string | null;
  newModelName?: string | null;
}

/**
 * Body for `POST /imports/commit` — shaped here so PM-6 builds it from the
 * wizard's lifted state. PM-5 does not call commit.
 */
export interface CommitImportBody {
  analysisToken?: string | null;
  projectId?: string | null;
  /** PM-D12 — create the project as part of the import (shape owned by
   *  CreateProjectRequest; PM-6 fills it from the create-project form). */
  project?: unknown;
  sheetName: string;
  headerRow: number;
  mapping: ImportColumnMapping[];
  rows: ImportAnalyzedRow[];
  modelResolutions?: ImportModelResolution[];
  allowOverride?: boolean;
  /** FB-32 — GRANULAR per-CELL override (projects/v1, additive; supersedes
   *  FB-30's `overrideRowIndexes`). The exact `(rowIndex, field)` pairs a
   *  tenant_admin chose to update base data for. When present this is the
   *  authoritative override list (supersedes `allowOverride`'s all-or-nothing):
   *  the BE overrides ONLY these (row, field) base-data values for a
   *  matched-differing row; un-listed diff fields keep existing base data
   *  (FB-28). A non-admin supplying it is ignored server-side. */
  overrideFields?: { rowIndex: number; field: string }[];
}

/** 202 response for `POST /imports/commit` — the async job handle. */
export interface CommitImportResponse {
  importId: string;
}

/** Per-row commit outcome (PM-D19). */
export const IMPORT_ROW_OUTCOMES = [
  "created",
  "matched_unchanged",
  "blocked",
  "overridden",
] as const;
export type ImportRowOutcome = (typeof IMPORT_ROW_OUTCOMES)[number];

/** One per-row commit result (PM-D19). `reason` explains a `blocked` or
 *  `overridden` row. */
export interface ImportRowResult {
  rowIndex: number;
  outcome: ImportRowOutcome;
  reason?: string | null;
  assetId?: string | null;
  contractPositionId?: string | null;
}

/** Live commit progress (the bottom-overlay surface, PM-D19). */
export interface ImportProgress {
  processed: number;
  total: number;
  created?: number;
  blocked?: number;
  overridden?: number;
}

/** Terminal/running job status (`running | done | cancelled | failed`). */
export const IMPORT_JOB_STATUSES = [
  "running",
  "done",
  "cancelled",
  "failed",
] as const;
export type ImportJobStatusValue = (typeof IMPORT_JOB_STATUSES)[number];

/** Summary of what the run wrote (PM-D12). */
export interface ImportCreatedEntities {
  projectId?: string | null;
  contractIds?: string[];
  siteIds?: string[];
  assetIds?: string[];
}

/** Status + per-row result report for `GET /imports/{importId}` (PM-D19).
 *  `rows` fills in as the staged commit proceeds; `error` is set on
 *  `status=failed`. */
export interface ImportJobStatus {
  importId: string;
  status: ImportJobStatusValue;
  progress: ImportProgress;
  rows: ImportRowResult[];
  createdEntities?: ImportCreatedEntities;
  error?: string | null;
}

/* ── FE-side field catalogue (PM-D2/D3/D22, mapping picker options) ────── */

/** The import field keys the mapping step offers (PM-D15 description list).
 *  `null` (ignore) is handled separately by the picker, not listed here. */
export const IMPORT_FIELD_KEYS = [
  "identifier",
  "systemType",
  "siteName",
  "latitude",
  "longitude",
  "internalParkNumber",
  "bladeSerialA",
  "bladeSerialB",
  "bladeSerialC",
  "bladeType",
  "operator",
  "externalIdContract",
  "externalIdContractPosition",
  "hubHeight",
] as const;
export type ImportFieldKey = (typeof IMPORT_FIELD_KEYS)[number];

/** The three required fields per row (PM-D2). A row missing any of these
 *  cannot import (`hard_error`). Surfaced as a hint in the mapping step. */
export const REQUIRED_IMPORT_FIELDS: readonly ImportFieldKey[] = [
  "identifier",
  "systemType",
  "siteName",
];

/** Human labels for the mapping picker + grid headers. */
export const IMPORT_FIELD_LABELS: Record<ImportFieldKey, string> = {
  identifier: "Turbine identifier",
  systemType: "System type (model)",
  siteName: "Park name",
  latitude: "Latitude",
  longitude: "Longitude",
  internalParkNumber: "Internal park number",
  bladeSerialA: "Blade serial A",
  bladeSerialB: "Blade serial B",
  bladeSerialC: "Blade serial C",
  bladeType: "Blade type",
  operator: "Operator",
  externalIdContract: "External ID (Contract)",
  externalIdContractPosition: "External ID (Contract Position)",
  hubHeight: "Hub height",
};
